import React, {useMemo, useState} from 'react'
import {Bar} from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import {useSelector} from 'react-redux'
import {useGetNetProfitQuery} from '../../redux/network/AdminDashboardApi'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const NetProfitBarChart = () => {
  const { startDate, endDate, customers, isFiltered } = useSelector((state: any) => state.Filter.Filter);

  const adjustedStartDate = useMemo(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dayDifference = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  
    if (dayDifference < 7) {
      const newStart = new Date(end);
      newStart.setDate(end.getDate() - 7);
      return newStart.toISOString().split('T')[0];
    }
    
    return startDate;
  }, [startDate, endDate]);

const { data: netProfitData, error: netProfitError } = useGetNetProfitQuery(
  {startDate: adjustedStartDate, endDate, customers },
  {
    refetchOnMountOrArgChange: true,
    pollingInterval: 0,
    skip: false,
  }
);

  const data = useMemo(
    () => ({
      labels : netProfitData?.labels ?? [],
      datasets: [
        {
          label: 'Profit',
          data: netProfitData?.profit ?? [],
          backgroundColor: 'rgba(181, 181, 195, 0.7)', // Gray color
          borderRadius: 6,
          categoryPercentage: 0.8, // Adjust category width
          barPercentage: 1.0, // Full width bars
        },
        {
          label: 'Loss',
          data: netProfitData?.loss ?? [],
          backgroundColor: 'rgba(236, 111, 102, 0.7)', // Red color
          borderRadius: 6,
          categoryPercentage: 0.8, // Adjust category width
          barPercentage: 1.0, // Full width bars
        },
      ],
    }),
    [netProfitData]
  )

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          afterBody: function (tooltipItems: any) {
            if (!netProfitData?.count || !netProfitData?.labels) {
              return '';
            }
            const hoveredIndex = tooltipItems[0].dataIndex;
            const shipmentCount = netProfitData.count[hoveredIndex];
            return shipmentCount !== undefined ? `Shipment Count: ${shipmentCount}` : '';
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        stacked: false,
        barThickness: 40, // Set bar width
        categoryPercentage: 0.8, // Reduce space between bars in the same group
        barPercentage: 1.0, // Maximize bar width
      },
      y: {
        title: {
          display: true,
          text: 'Amount',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        ticks: {
          beginAtZero: false,
          callback: function (tickValue: string | number) {
            return Number(tickValue).toLocaleString(); // Format the ticks
          },
        },
      },
    },
  };
  
  

  return (
    <div
      className='card mb-xl-8 w-100 card-xl-stretch'
      style={{border: '1px solid rgba(236, 111, 102, 0.1)'}}
    >
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold text-dark fs-2'>Net Profit/Loss</span>
          <span className='text-muted mt-1 fw-semibold fs-6'>Net Profit and Loss Summary</span>
        </h3>
      </div>
      <div className='card-body pt-0 d-flex align-items-end mt-5' style={{height: '350px'}}>
        <Bar data={data} options={options} />
      </div>
    </div>
  )
}

export default NetProfitBarChart
